import React from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import { addClass, hasClass, removeClass, purifier } from '../../../utils/dom';
import { imageSizeTranslation } from '../../../utils/image';
import { translate, prefix } from '../../../utils/string';
import s from './Media.scss';

const vimeoInitiated = false;

const vimeo = {
	playerOrigin: '*',
	init: () => {
		// Handle messages received from the player
		const onMessageReceived = (event) => {
			let data = {};

			// Handle messages from the vimeo player only
			if (!(/^https?:\/\/player.vimeo.com/).test(event.origin)) {
				return false;
			}

			if (vimeo.playerOrigin === '*') {
				vimeo.playerOrigin = event.origin;
			}

			if (typeof event.data !== 'object') {
				data = JSON.parse(event.data);
			}

			if (data.event === 'ready') {
				vimeo.ready();
			}

			return true;
		};

		// Listen for messages from the player
		if (window.addEventListener) {
			window.addEventListener('message', onMessageReceived, false);
		} else {
			window.attachEvent('onmessage', onMessageReceived, false);
		}
	},
	ready: () => {
		console.log('Vimeo is initiated');
	},
	post: (iframe, action, value) => {
		const data = {
			method: action,
		};
		if (value) data.value = value;

		iframe.contentWindow.postMessage(data, vimeo.playerOrigin);
	},
};

const videoClickHandler = (e) => {
	const $video = e.currentTarget.querySelector('video');
	if ($video) {
		let videoPlaying = false;
		if (!$video.autoplay && !videoPlaying) {
			if (e.target === $video) e.preventDefault();
			$video.play();
			videoPlaying = true;
		} else if ($video.dataset.isMuted === undefined) {
			if (e.target === $video) e.preventDefault();
			$video.muted = !$video.muted;
		}
	}
};

const embedClickHandler = (e) => {
	const $link = e.currentTarget;
	if (!vimeoInitiated) vimeo.init();
	if ($link) {
		e.preventDefault();
		if (hasClass($link, 'is_active')) {
			removeClass($link, 'is_active');
			vimeo.post($link.nextSibling.querySelector('iframe'), 'pause');
		} else {
			addClass($link, 'is_active');
			vimeo.post($link.nextSibling.querySelector('iframe'), 'play');
		}
	}
};

const MediaCaptionLink = ({ content, setup, alreadyHasLink, linkNumber = '' }) => {
	const link = content[`linkText${linkNumber}`] ? translate(setup, content[`linkText${linkNumber}`]) : null;
	const linkStyles = {};
	const isButton = (content.linkStyles.background && content.linkStyles.background !== 'transparent') || (content.linkStyles.border && content.linkStyles.border !== 'transparent');

	if (content.linkStyles.background) linkStyles.backgroundColor = content.linkStyles.background;
	if (content.linkStyles.border) linkStyles.borderColor = content.linkStyles.border;
	if (content.linkStyles.text) linkStyles.color = content.linkStyles.text;

	if (link && !alreadyHasLink) {
		return <Link className={[s.mediaCaption_link, (isButton ? s.mediaCaption_link__button : null)].join(' ')} style={{ ...linkStyles }} to={prefix(setup, translate(setup, content[`link${linkNumber}`], 'url'))}><span>{ link }</span></Link>;
	}
	if (link) {
		return <span className={[s.mediaCaption_link, (isButton ? s.mediaCaption_link__button : null)].join(' ')} style={{ ...linkStyles }}><span>{ link }</span></span>;
	}
	return null;
};

const DoubleLink = ({ content, setup, alreadyHasLink }) => (
	<div className={s.mediaCaption_doubleLink}>
		<div className={s.mediaCaption_doubleLink_inner}>
			<MediaCaptionLink content={content} setup={setup} alreadyHasLink={alreadyHasLink} />
		</div>
		<div className={s.mediaCaption_doubleLink_inner}>
			<MediaCaptionLink content={content} setup={setup} alreadyHasLink={alreadyHasLink} linkNumber="2" />
		</div>
	</div>
);

const MediaCaption = ({ content, setup, alreadyHasLink = false }) => {
	const HeaderTag = content.headerDetails.type;
	const SubHeaderTag = content.subHeaderDetails.type;
	if (!content.headerText && !content.linkText) return null;
	const mediaCaptionClasses = [s.mediaCaption, s[`mediaCaption__${content.textDetails.origin.horizontal}`], s[`mediaCaption__${content.textDetails.origin.vertical}`]];
	if (content.doubleLink) mediaCaptionClasses.push(s.mediaCaption__double);

	return (
		<div className={mediaCaptionClasses.join(' ')} style={{ color: content.textDetails.color, backgroundColor: (content.caption ? content.textDetails.background : 'transparent') }}>
			<div className={s.mediaCaption_inner} style={{ backgroundColor: content.textDetails.background }}>
				{ content.headerText ? <HeaderTag className={[s.mediaCaption_headline, `u_font__${content.headerDetails.font}`, `u_fontSize__${content.headerDetails.size}`].join(' ')}>{ translate(setup, content.headerText) }</HeaderTag> : null }
				{ content.subHeaderText ? <SubHeaderTag className={[s.mediaCaption_headline, `u_font__${content.subHeaderDetails.font}`, `u_fontSize__${content.subHeaderDetails.size}`].join(' ')}>{ translate(setup, content.subHeaderText) }</SubHeaderTag> : null }
				{ content.doubleLink ? <DoubleLink content={content} setup={setup} alreadyHasLink={alreadyHasLink} /> : <MediaCaptionLink content={content} setup={setup} alreadyHasLink={alreadyHasLink} /> }
			</div>
		</div>
	);
};

const MediaContent = ({ content, columnWidth }) => {
	if (content.media && /(jpe?g|png|svg|gif)$/.test(content.media.url.toLowerCase())) {
		// Is image
		const responsiveImages = imageSizeTranslation(columnWidth, content.media, Math.abs(content.media.width / content.media.height) > 1.2);
		const img = <img className={[s.mediaInner_image, (content.media.url.indexOf('.svg') !== -1 ? s.mediaInner_image__svg : null), s[`mediaInner_image__${content.mediaDetails.origin.horizontal}`], s[`mediaInner_image__${content.mediaDetails.origin.vertical}`]].join(' ')} {...responsiveImages} />;
		return !content.link && content.videoDetails.embed ? <a href="#controlEmbed" onClick={(e) => embedClickHandler(e)}>{ img }</a> : img;
	}
	if ((content.media && /mp4$/.test(content.media.url.toLowerCase())) || content.videoDetailsVideo) {
		const filmFile = content?.videoDetailsVideo?.url || content?.media?.url || '';
		const dataAttrs = content.videoDetailsMute ? { 'data-is-muted': true } : {};
		const video = <video className={[s.mediaInner_video, s[`mediaInner_image__${content.mediaDetails.origin.horizontal}`], s[`mediaInner_image__${content.mediaDetails.origin.vertical}`]].join(' ')} src={filmFile} autoPlay={content.videoDetailsAutoplay} loop={content.videoDetailsLoop} playsInline muted={content.videoDetailsAutoplay ? 1 : null} {...dataAttrs} />;
		return !content.link ? <a href="#controlVideo" onClick={videoClickHandler}>{ video }</a> : video;
	}
	return null;
};

const MediaInner = ({ content, columnWidth, setup, alreadyHasLink = false }) => (
	<div className={[s.mediaInner, (content.mediaDetails.cropOnMobile ? s.mediaInner__crop : '')].join(' ')}>
		{ content.media || content.videoDetailsVideo ? <MediaContent content={content} columnWidth={columnWidth} /> : null }
		{ content.videoDetails.embed ? <div className={s.mediaInner_embed} dangerouslySetInnerHTML={{ __html: purifier(content.videoDetails.embed, { ALLOWED_TAGS: ['iframe'] }) }} /> : null }
		{ !content.caption ? <div className={s.mediaInner_caption}><MediaCaption content={content} setup={setup} alreadyHasLink={alreadyHasLink} onTop /></div> : null }
	</div>
);

const Media = ({ content, columnWidth, setup }) => (
	content.link && !content.doubleLink ? (
		<Link className={s.media} onClick={videoClickHandler} to={prefix(setup, translate(setup, content.link, 'url'))}>
			<MediaInner content={content} columnWidth={columnWidth} setup={setup} alreadyHasLink />
			{ content.caption ? <MediaCaption content={content} setup={setup} alreadyHasLink /> : null }
		</Link>
	) : (
		<div className={s.media}>
			<MediaInner content={content} columnWidth={columnWidth} setup={setup} />
			{ content.caption ? <MediaCaption content={content} setup={setup} /> : null }
		</div>
	)
);

export default hot(module)(Media);
